import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'

export interface ServerCartProduct {
  id: string
  name: string
  category: string
  price: number
  listPrice: number
  shippingFee?: number
  shippingCharge?: string
  shippingEvery?: number
  sellUnit?: string
  imageShows?: string
  packQty?: number
  image: string
  thumbnail?: string
  unit: string
  stock: number
  status: string
  storeName: string
  minOrder?: number
  orderStep?: number
  maxOrder?: number
}

export interface ServerCartItem {
  idx: number
  productId: string
  qty: number
  optionName?: string
  optionExtra?: number
  key: string
  unitPrice: number
  baseUnitPrice?: number
  listUnit?: number
  productOff?: number
  orgOff?: number
  lineTotal: number
  productDiscountTotal?: number
  orgDiscountTotal?: number
  discountPct?: number
  snapshotPrice?: number | null
  priceChanged?: boolean
  soldOut?: boolean
  stockShort?: boolean
  minOrder?: number
  orderStep?: number
  maxOrder?: number
  product: ServerCartProduct
}

export interface ServerCart {
  items: ServerCartItem[]
  itemCount: number
  productCount: number
  subtotal: number
  listSubtotal?: number
  productDiscountTotal?: number
  orgDiscountTotal?: number
  shippingFee: number
  payable: number
  discountPct?: number
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/mall/cart`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다.')
  }
  return (await response.json()) as ApiResponse<T>
}

function unwrap(result: ApiResponse<ServerCart>, fallback: string): ServerCart {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

export async function fetchServerCart() {
  const response = await apiFetch(BASE, fetchOptions)
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '장바구니를 불러오지 못했습니다.')
}

export async function addServerCartItem(payload: {
  productId: string
  qty?: number
  optionName?: string
  optionExtra?: number
  /** add: 기존 수량에 더함(기본). set: 수량을 덮어씀(바로구매) */
  mode?: 'add' | 'set'
}) {
  const response = await apiFetch(`${BASE}/items`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '장바구니에 담지 못했습니다.')
}

export async function updateServerCartItem(
  idx: number,
  payload: number | { qty?: number; optionName?: string; optionExtra?: number },
) {
  const body = typeof payload === 'number' ? { qty: payload } : payload
  const response = await apiFetch(`${BASE}/items/${idx}`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '장바구니를 바꾸지 못했습니다.')
}

export async function removeServerCartItem(idx: number) {
  const response = await apiFetch(`${BASE}/items/${idx}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '항목을 삭제하지 못했습니다.')
}

export async function clearServerCart() {
  const response = await apiFetch(BASE, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '장바구니를 비우지 못했습니다.')
}

export async function mergeServerCart(
  items: Array<{ productId: string; qty: number; optionName?: string; optionExtra?: number }>,
) {
  const response = await apiFetch(`${BASE}/merge`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ items }),
  })
  const result = await parseJson<ServerCart>(response)
  return unwrap(result, '장바구니를 합치지 못했습니다.')
}
